import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useChatScrolling } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useChatScrolling";
import useApplyCompetitorsMutation from "app/pages/sales-intelligence/modules/ai-agent/queries/useApplyCompetitorsMutation";
import useCancelCompetitorsMutation from "app/pages/sales-intelligence/modules/ai-agent/queries/useCancelCompetitorsMutation";
import ChartStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/chart/chartService";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { addAgentMessage, addHumanMessage } from "app/pages/sales-intelligence/modules/ai-agent/store";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import { SUGGESTION_TEXTS } from "app/pages/sales-intelligence/pages/ai-agent/constants";
import { useEmailRefineChat } from "app/pages/sales-intelligence/pages/ai-agent/hooks/useEmailRefineChat";
import { areEqualArrays } from "app/pages/sales-intelligence/utils/arrays";

import { AIAgentEmailRefinePanelContent } from "./AIAgentEmailRefinePanelContent";
import { AIAgentEmailRefinePanelFooter } from "./AIAgentEmailRefinePanelFooter";

export type AIAgentEmailRefineProps = {
    isLoading: boolean;
    isUpdatingVersion: boolean;
    emailPreviews?: ParsedAIEmailPreview[];
    selectedCompany: AiAgentEmailAutocompleteCompany | null;
    versions: ParsedAIEmailPreview[];
    onAddVersion: (preview: ParsedAIEmailPreview, withApplyMutation?: boolean) => void;
    threadId: string;
    onEmailGenerationStop: () => void;
    competitors: Competitor[];
    onSetEmailPreviews: (previews: ParsedAIEmailPreview[]) => void;
};

const COMPETITOR_SUGGESTION_BOTTOM_OFFSET = 312;
const VISUAL_SUGGESTION_BOTTOM_OFFSET = 843;

export const AIAgentEmailRefinePanel = ({
    isUpdatingVersion,
    isLoading: isLoadingThread,
    emailPreviews,
    selectedCompany,
    versions,
    onAddVersion,
    threadId,
    onEmailGenerationStop,
    competitors: competitorsFromCurrentChart,
    onSetEmailPreviews,
}: AIAgentEmailRefineProps) => {
    const dispatch = useDispatch();
    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);
    const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
    const lastAiMessageRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [isPreviewsShown, setIsPreviewsShown] = useState(false);
    const [isCompetitorsShown, setIsCompetitorsShown] = useState(false);
    const hasCompetitorsChanged = useRef(false);

    const { mutate: cancelCompetitors } = useCancelCompetitorsMutation();
    const {
        isFetching: isLoadingCompetitors,
        stop: onStopApplyingCompetitors,
        mutate,
        reset: resetApplyCompetitors,
    } = useApplyCompetitorsMutation({
        onAddVersion,
    });

    const { scrollChatWhenAiResponse, scrollUserMessageToTop, removeSpacer } = useChatScrolling({
        containerRef,
        lastAiMessageRef,
        lastUserMessageRef,
        bottomRef,
    });

    const handleScrollUserMessageToTop = (bottomOffset?: number) => {
        requestAnimationFrame(() => {
            scrollUserMessageToTop(bottomOffset ?? 0, 0);
        });
    };

    const handleScrollAiResponseToBottom = () => {
        requestAnimationFrame(() => scrollChatWhenAiResponse());
    };

    const handleCompetitorsShow = useCallback(() => {
        setIsCompetitorsShown(true);
    }, []);

    const handlePreviewsShow = useCallback(() => {
        setIsPreviewsShown(true);
    }, []);

    const {
        localText,
        onTextChange,
        isLoadingChat,
        onSubmitChat,
        onStopChat,
        competitors,
        onCompetitorsChange,
        onRegenerateVisuals,
    } = useEmailRefineChat({
        threadId,
        onAddVersion,
        onScrollUserMessageToTop: handleScrollUserMessageToTop,
        onScrollAiResponseToBottom: handleScrollAiResponseToBottom,
        onShowCompetitors: handleCompetitorsShow,
        onShowPreviews: handlePreviewsShow,
        onSetEmailPreviews,
    });

    const handleApplyVisual = useCallback(
        (preview: ParsedAIEmailPreview) => {
            onAddVersion(preview, true);
            AIAgentTrackingService.email.trackSelectVisual(ChartStreamService.getChartTitle(preview.chartJson));
            setIsPreviewsShown(false);
        },
        [onAddVersion],
    );

    const handleVersionClick = useCallback(
        (id: string) => {
            const versionToApply = versions[Number(id)];
            if (!versionToApply) {
                return;
            }
            AIAgentTrackingService.email.trackRevertVersion();
            onAddVersion(versionToApply, true);
        },
        [versions, onAddVersion],
    );

    const handlePresentVisuals = useCallback(() => {
        if (hasCompetitorsChanged.current || !emailPreviews || emailPreviews.length === 0) {
            onRegenerateVisuals();
            hasCompetitorsChanged.current = false;
        } else {
            dispatch(addHumanMessage(translate(SUGGESTION_TEXTS[0])));
            handleScrollUserMessageToTop(VISUAL_SUGGESTION_BOTTOM_OFFSET);
            handlePreviewsShow();
        }
    }, [onSubmitChat, onRegenerateVisuals, emailPreviews]);

    const handleApplyCompetitorsSuggestion = useCallback(() => {
        onCompetitorsChange(competitorsFromCurrentChart);
        setIsPreviewsShown(false);
        setIsCompetitorsShown(true);
        handleScrollUserMessageToTop(COMPETITOR_SUGGESTION_BOTTOM_OFFSET);
    }, [competitorsFromCurrentChart]);

    const handleApplyCompetitors = useCallback(() => {
        setIsCompetitorsShown(false);
        hasCompetitorsChanged.current = true;
        mutate({ competitors, threadId });
        AIAgentTrackingService.email.trackApplyCompetitors(competitors.length);
    }, [competitors, threadId]);

    const handleCancelCompetitors = useCallback(() => {
        resetApplyCompetitors();
        setIsCompetitorsShown(false);
        cancelCompetitors({ threadId });
    }, [threadId]);

    const handleSubmitChat = useCallback(() => {
        if (isPreviewsShown) {
            setIsPreviewsShown(false);
        }
        onSubmitChat();
    }, [onSubmitChat, isPreviewsShown]);

    const isLoadingState = useMemo(() => {
        return isLoadingThread || isLoadingChat || isUpdatingVersion || isLoadingCompetitors;
    }, [isLoadingThread, isLoadingChat, isUpdatingVersion, isLoadingCompetitors]);

    const handleSubmitPanel = useCallback(() => {
        if (isLoadingState) {
            AIAgentTrackingService.email.trackPauseAgent();
        }
        if (isLoadingCompetitors) {
            void onStopApplyingCompetitors();
            return;
        }

        if (isLoadingChat) {
            void onStopChat();
            return;
        }

        if (isLoadingThread || isUpdatingVersion) {
            onEmailGenerationStop();
            return;
        }
        handleSubmitChat();
    }, [isLoadingState, isLoadingCompetitors, isLoadingChat, isLoadingThread, isUpdatingVersion, handleSubmitChat]);

    useEffect(() => {
        if (isUpdatingVersion) {
            removeSpacer();
        } else {
            handleScrollAiResponseToBottom();
        }
    }, [isUpdatingVersion]);

    useEffect(() => {
        if (isPreviewsShown && emailPreviews && emailPreviews.length !== 0) {
            removeSpacer();
        }
    }, [isPreviewsShown, emailPreviews]);

    useEffect(() => {
        if (!isLoadingThread) {
            dispatch(
                addAgentMessage({ content: translate("si.pages.ai_agent_email.start_message"), id: "start_message" }),
            );
        }
    }, [isLoadingThread]);

    const isSuggestionsVisible = useMemo(() => {
        return !isLoadingState && !isCompetitorsShown && !isBlocked;
    }, [isLoadingState, isCompetitorsShown, isBlocked]);

    const isApplyCompetitorsDisabled = useMemo(() => {
        return competitors.length === 0 || areEqualArrays(competitors, competitorsFromCurrentChart);
    }, [competitors, competitorsFromCurrentChart]);

    return (
        <>
            <Box
                overflowY="auto"
                ref={containerRef}
                minHeight="calc(100% - 50px)"
                padding="0 12px 0 0"
            >
                <AIAgentEmailRefinePanelContent
                    isSuggestionsVisible={isSuggestionsVisible}
                    isLoading={isLoadingThread}
                    isLoadingMessages={isLoadingChat || isLoadingCompetitors}
                    isUpdatingVersion={isUpdatingVersion}
                    isCompetitorsShown={isCompetitorsShown}
                    emailPreviews={emailPreviews}
                    selectedCompany={selectedCompany}
                    competitors={competitors}
                    onCompetitorsChange={onCompetitorsChange}
                    lastUserMessageRef={lastUserMessageRef}
                    lastAiMessageRef={lastAiMessageRef}
                    isPreviewsShown={isPreviewsShown}
                    onApplyVisual={handleApplyVisual}
                    onPresentVisuals={handlePresentVisuals}
                    onApplyCompetitorsSuggestion={handleApplyCompetitorsSuggestion}
                    onVersionClick={handleVersionClick}
                />
                <Box ref={bottomRef} />
            </Box>
            <AIAgentEmailRefinePanelFooter
                isLoading={isLoadingState}
                isBlocked={isBlocked}
                isCompetitorsShown={isCompetitorsShown}
                onCancelCompetitors={handleCancelCompetitors}
                onApplyCompetitors={handleApplyCompetitors}
                localText={localText}
                isApplyCompetitorsDisabled={isApplyCompetitorsDisabled}
                onTextChange={onTextChange}
                onSubmitChat={handleSubmitChat}
                onSubmitPanel={handleSubmitPanel}
            />
        </>
    );
};
